/**
 * How a SECTION names itself (ADR 0066).
 *
 * The owner works with the Design tab's section list in one hand and the
 * elevation drawing in the other, and reported that the rows are "near-identical
 * stacked rows" — on a three-section wall you match a row to the drawing by
 * POSITION, which is exactly the kind of counting a tool should be doing.
 *
 * The fix is not a new label; it is ONE label definition read by both surfaces.
 * `sectionCodeLabel()` is the elevation's own caption (it was an inline closure
 * in elevation.js until this ADR), and the section row now renders the same
 * string — so a row and the shape it draws can never say different things, and
 * a future change to the caption reaches both without anyone remembering.
 *
 * DOM-free on purpose: app.js and elevation.js own their own elements, this
 * module owns what the text says.
 */

/** Formats a length the way the caller's app does, with the elevation's own
 *  whole-inch fallback for a caller that passes nothing. */
function len(units, formatLength) {
  return formatLength ? formatLength(units) : `${Math.round(units)}"`;
}

/**
 * `SV-16"` — a section's type code with its DEPTH (owner, 2026-08-04). Depth is
 * the one dimension an elevation cannot show geometrically, which is why it is
 * worth the characters. The figure is the RESOLVED depth of the placed unit
 * (own depth over a base, inherited above a non-base), so a 16" shelf on a 22"
 * base reads 16 rather than repeating the base.
 *
 * A section with no resolved depth reads as the bare code rather than inventing
 * a figure — an unplaced section genuinely has no depth to state.
 *
 * @param {string|null|undefined} typeCode
 * @param {number} depthUnits resolved depth of the placed unit
 * @param {(units:number)=>string} [formatLength]
 * @returns {string}
 */
export function sectionCodeLabel(typeCode, depthUnits, formatLength) {
  const code = typeCode ?? '?';
  if (!(depthUnits > 0)) return code;
  return `${code}-${len(depthUnits, formatLength)}`;
}

/**
 * What ONE section row says it is (ADR 0066 item 6): the elevation's code
 * caption plus the WIDTH the elevation prints below the floor line — the two
 * captions a designer reads off the drawing, in the same words, so a row is
 * recognisable against it without counting positions.
 *
 * Width comes from the SOLVED layout item (`openingUnits`), not from the
 * section's stored width, because an auto-filled section has no stored width
 * and the drawing shows what the solver produced.
 *
 * @param {{typeCode?:string, depthUnits?:number, widthUnits?:number}} spec
 * @param {(units:number)=>string} [formatLength]
 * @returns {{code:string, width:string|null, label:string}}
 */
export function sectionRowIdentity({ typeCode, depthUnits, widthUnits } = {}, formatLength) {
  const code = sectionCodeLabel(typeCode, depthUnits, formatLength);
  const width = widthUnits > 0 ? len(widthUnits, formatLength) : null;
  return { code, width, label: width ? `${code} · ${width}` : code };
}
